// AuthenticationForm.js
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Stack,
  Typography,
  Box,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AvisoWhatsapp from "../../components/comunicaciones/AvisoWhatsapp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useCurrentUser } from "../../components/context/UserContext";
import { register, login, logout } from "../../helpers/authentication-helpers";
import { confirmarMantenimiento } from "../../helpers/mantenimiento";
import { useLocation, Navigate, useNavigate, Link as RouterLink } from "react-router-dom";
import GoogleSignInButton from "./GoogleSignInButton";
import ConnectionDiagnostics from "./ConnectionDiagnostics";
import RichMarkdown from "../../components/markdown/RichMarkdownDiferido"; // #1717 R12: el motor de markdown baja al abrir los Términos
import { getTosMarkdown, TOS_VERSION_VIGENTE } from "../../content/tosContent";
import {
  PAISES,
  banderaPais,
  mensajeCelularInvalido,
  normalizeMobile,
  placeholderCelular,
  prefijoCelular,
} from "../../helpers/phone";
import { destinoSegunCuenta } from "../../helpers/destino-sesion";
import { pideVerificacion } from "../../helpers/pasos-verificacion";
import {
  fieldLabelSx,
  inputSx,
  looksLikeEmail,
  primaryBtnSx,
  SLOW_REQUEST_MS as SLOW_LOGIN_MS,
} from "./auth-styles";
import { authModalRootEl } from "./AuthShell";

const WA_URL =
  "https://wa.me/56968426546?text=Hola%20Sebasti%C3%A1n%2C%20quiero%20probar%20SmartDx";
const MAIL_URL = "mailto:sebastian@smardx.com?subject=Quiero%20probar%20SmartDx";

const CONTACT_CHANNELS = [
  { icon: <WhatsAppIcon fontSize="small" />, label: "WhatsApp", href: WA_URL, external: true },
  { icon: <MailOutlineIcon fontSize="small" />, label: "Correo", href: MAIL_URL, external: false },
  // "Agendar" navega al Home React (/) con hash #contacto → Home.js reenvía el
  // postMessage al iframe de la home. NO usar /home_v3.html#contacto: la v3 ya no se
  // publica (#1681) y esa ruta solo sobrevive como 301 a /; apuntar directo a / con el hash.
  { icon: <CalendarMonthOutlinedIcon fontSize="small" />, label: "Agendar", route: "/", hash: "#contacto" },
];

// El login del backend autentica por CORREO (AuthTokenSerializer.email es un
// EmailField): si llega el nombre de usuario da 400 y el form mostraba el
// genérico "Usuario o contraseña incorrectos". Chequeo mínimo de forma para
// poder decirle al médico qué pasó — la validación real la hace el backend.
// A los 8s el login deja de sentirse "instantáneo con un tirón" y empieza a
// sentirse roto. Ahí avisamos y sondeamos la red, sin cortar el intento.
const channelBtnSx = {
  flex: 1,
  minWidth: 0,
  minHeight: 44,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13.5,
  gap: 0.75,
  py: 1,
  borderRadius: "12px",
  color: "text.primary",
  border: "1px solid rgba(var(--fg-rgb),0.16)",
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": { color: "text.secondary" },
  "&:hover": {
    backgroundColor: "rgba(var(--fg-rgb),0.08)",
    borderColor: "rgba(var(--fg-rgb),0.28)",
  },
};

// #1439 (mockup tools/mockups/smartdx_mockups_navbar_acceso_e_v2.html): el
// formulario vive en un panel de vidrio SIEMPRE oscuro (AuthShell). Los campos
// son translúcidos y el botón de enviar es una píldora delineada: el camino
// principal es Google (#1453), que va primero y en blanco.
const inputVidrioSx = {
  ...inputSx,
  backgroundColor: "rgba(var(--fg-rgb),0.08)",
  borderRadius: "14px",
  minHeight: 50,
};
const enviarSx = {
  ...primaryBtnSx,
  minHeight: 50,
  borderRadius: "999px",
  color: "text.primary",
  backgroundColor: "transparent",
  border: "1px solid rgba(var(--fg-rgb),0.3)",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "rgba(var(--fg-rgb),0.08)",
    borderColor: "rgba(var(--fg-rgb),0.5)",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "rgba(var(--fg-rgb),0.4)",
    borderColor: "rgba(var(--fg-rgb),0.14)",
    backgroundColor: "transparent",
  },
};
const pestanaSx = (activa) => ({
  border: 0,
  cursor: "pointer",
  font: "inherit",
  fontSize: 14.5,
  fontWeight: 500,
  minHeight: 44,
  borderRadius: "999px",
  color: activa ? "var(--bg-base)" : "rgba(var(--fg-rgb),0.7)",
  backgroundColor: activa ? "var(--text-primary)" : "transparent",
  transition: "background-color 160ms ease, color 160ms ease",
  "&:focus-visible": { outline: "2px solid var(--color-primary-soft)", outlineOffset: 2 },
});

// #1453 R8: las reglas de la clave que se pueden saber sin preguntarle al
// servidor, en vivo. Son un subconjunto de AUTH_PASSWORD_VALIDATORS del
// backend (mínimo 8, no solo números, no parecida al correo); la de "clave
// común" solo la sabe el servidor y llega en español por `registro-error`.
export const reglasDeClave = (password = "", email = "") => {
  const local = (String(email).split("@")[0] || "").trim().toLowerCase();
  const clave = String(password);
  return [
    { id: "largo", ok: clave.length >= 8, texto: "Mínimo 8 caracteres" },
    { id: "no-solo-numeros", ok: clave.length > 0 && !/^\d+$/.test(clave), texto: "No solo números" },
    {
      id: "no-correo",
      ok: !(local.length >= 3 && clave.toLowerCase().includes(local)),
      texto: "Que no contenga tu correo",
    },
  ];
};

// Google solo se dibuja con client id (GoogleSignInButton.js); sin él no
// tiene sentido el separador "o con tu correo".
const GOOGLE_DISPONIBLE = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

// #1453 (30-sep): el aviso de la casilla sale DENTRO de la casilla, no al
// fondo del formulario (ahí quedaba fuera de pantalla en el celular y el
// médico creía que la página no funcionaba).
const TOS_FALTA = "Marca esta casilla para crear tu cuenta.";
const TOS_CUENTA_NUEVA_GOOGLE =
  "Tu cuenta de Google todavía no tiene cuenta en SmartDx. Marca la casilla de Términos para crearla.";

// Valores del pop-up de la home v4 (home_v4.html, OPTS). "informes" no llega
// acá: ese camino va a "Conversemos".
const MOTIVO_TEXTO = {
  sala: "Sala",
  uti: "UTI",
  uci: "UCI",
  // "utim" lo ofrecía el pop-up hasta el 14-oct; un link o una visita guardada con esa
  // clave sigue mostrando la unidad, ya con el nombre correcto (UTI = intermedios).
  utim: "UTI",
  consulta_ambulatoria: "Consulta ambulatoria",
  urgencias: "Urgencias",
};
const UNIDADES_MOTIVO = Object.keys(MOTIVO_TEXTO);

// La unidad que eligió en el pop-up de la home: primero la URL, si no la que quedó guardada.
const motivoActual = (search) => {
  const params = new URLSearchParams(search);
  // Si la URL trae `unidad`, manda la URL aunque no sea válida (ej. "informes"):
  // mostrar la unidad guardada de otra visita sería contradecir el link.
  if (params.has("unidad")) {
    const deUrl = params.get("unidad");
    return UNIDADES_MOTIVO.includes(deUrl) ? deUrl : null;
  }
  try {
    const guardado = window.sessionStorage.getItem("sdx-motivo");
    return UNIDADES_MOTIVO.includes(guardado) ? guardado : null;
  } catch (e) {
    return null;
  }
};

const AuthenticationForm = () => {
  const navigate = useNavigate();
  const [registrationToggle, setRegistrationToggle] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [loginPending, setLoginPending] = useState(false);
  const [loginSlow, setLoginSlow] = useState(false);
  const [loginFailureKind, setLoginFailureKind] = useState(null);
  const [loginAttempt, setLoginAttempt] = useState(0);
  const [openTermsModal, setOpenTermsModal] = useState(false);
  // #1453 R11 v2: en el login la casilla NO se muestra de entrada (una cuenta
  // existente entra con Google sin ella). Si el servidor contesta que la
  // cuenta es nueva (428 `requiere_terminos`, sin crearla), acá queda la
  // función que reenvía el mismo id_token, y recién ahí aparece la casilla.
  const [crearCuentaGoogle, setCrearCuentaGoogle] = useState(null);
  // #1453 (30-sep): se intentó avanzar sin la casilla → se resalta en rojo con
  // su aviso adentro. `googlePendienteRef` = reenvío del id_token que Google ya
  // entregó: al marcar la casilla el alta sigue sola, sin repetir Google.
  // Es un ref y no estado A PROPÓSITO: con estado, si la respuesta de otra
  // cuenta (B) y el click en la casilla caían juntos, React procesaba primero
  // el click (prioridad discreta) y disparaba el reenvío viejo de A antes de
  // aplicar el "borrar pendiente" de B: se creaba la cuenta equivocada
  // (revisor #1453). El ref se borra en el acto.
  const [tosResaltada, setTosResaltada] = useState(false);
  const googlePendienteRef = useRef(null);
  const casillaRef = useRef(null);
  const [emailTouched, setEmailTouched] = useState(false);
  // #1401: celular obligatorio en el registro (R16). `phoneTouched` sigue el
  // mismo patrón que `emailTouched` — no se avisa mientras la persona
  // todavía está escribiendo, solo al salir del campo o al intentar enviar.
  const [phoneTouched, setPhoneTouched] = useState(false);
  // #1453 R6: sin nombre de usuario (el backend lo deriva del correo).
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
    // #1590: país del médico (Chile por defecto). Cambia el prefijo y la
    // regla del celular; viaja al backend en `register()`.
    country: "CL",
    acceptTerms: false,
  });
  // #1585: "Repite la contraseña" y el ojito. La repetición vive FUERA de
  // formData a propósito: formData entero viaja a /api/user/create/ en
  // register(), y el backend no tiene por qué recibir la clave dos veces.
  // Un solo `claveVisible` para los dos campos: el médico compara lo que
  // escribió en uno contra el otro.
  const [repetirClave, setRepetirClave] = useState("");
  const [claveVisible, setClaveVisible] = useState(false);

  const { currentUser } = useCurrentUser();
  const location = useLocation();

  // #1439: la URL manda (`?register=true` = Crear cuenta). Las pestañas y los
  // botones "Ingresar" / "Crear cuenta gratis" del navbar cambian la URL y este
  // efecto ajusta el formulario. Al cambiar de modo se descarta lo pendiente
  // de Google, igual que hacía el link de abajo que reemplazan las pestañas.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const registro = params.get("register") === "true";
    setRegistrationToggle((antes) => {
      if (antes !== registro) {
        // #1585: la clave no sigue a la vista al cambiar de pestaña
        // (computador compartido del hospital).
        setClaveVisible(false);
        setCrearCuentaGoogle(null);
        googlePendienteRef.current = null;
        setTosResaltada(false);
      }
      return registro;
    });
  }, [location.search]);

  const cambiarModo = (registro) => {
    if (registro === registrationToggle) return;
    // Solo cambia `register`: el resto de la URL (ej. `unidad`) se conserva.
    const params = new URLSearchParams(location.search);
    if (registro) params.set("register", "true");
    else params.delete("register");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
  };

  // #1439: el pop-up "Qué te trae a SmartDx?" de la home v4 llega con
  // `?unidad=sala|uti|...`. Se guarda en la misma clave que usa la landing
  // (`sdx-motivo`, sessionStorage) para que no se pierda si el médico entró
  // por un link directo y no por el pop-up. Lo muestra el chip de Crear cuenta
  // (#1439 C, `motivoActual`); no se manda al backend (no hay campo). El
  // onboarding (#1525) lo puede usar, y tiene que esperar también "informes"
  // (la landing lo guarda ella misma).
  useEffect(() => {
    const unidad = new URLSearchParams(location.search).get("unidad");
    if (!unidad || !UNIDADES_MOTIVO.includes(unidad)) return;
    try {
      window.sessionStorage.setItem("sdx-motivo", unidad);
    } catch (e) {
      /* sessionStorage puede fallar en Safari privado: el dato es opcional */
    }
  }, [location.search]);

  const reglas = reglasDeClave(formData.password || "", formData.email || "");
  const claveCumple = reglas.every((r) => r.ok);
  const claveCoincide = (formData.password || "") === repetirClave;

  const isFormValid = () => {
    const email = formData.email || "";
    const password = formData.password || "";
    if (registrationToggle) {
      return Boolean(
        email.trim() &&
          password.trim() &&
          claveCumple &&
          claveCoincide &&
          normalizeMobile(formData.phone, formData.country),
      );
    }
    return email.trim() && password.trim();
  };

  const submitRegistration = (event) => {
    event.preventDefault();
    // La casilla ya no deshabilita "Crear cuenta" (un botón gris sin razón
    // visible era el mismo "no funciona"): se deja apretar y se señala.
    if (!formData.acceptTerms) {
      pedirCasilla();
      return;
    }
    if (!looksLikeEmail(formData.email)) {
      setEmailTouched(true);
      setErrorMessage("Revisa el correo electrónico: no parece una dirección válida.");
      return;
    }
    // #1453 R8: con una regla local sin cumplir no se manda nada.
    if (!claveCumple) {
      setErrorMessage("Revisa la contraseña: todavía no cumple las reglas de abajo.");
      return;
    }
    if (!claveCoincide) {
      setErrorMessage("Las dos contraseñas no coinciden.");
      return;
    }
    // #1401, R16: el celular se exige y se normaliza ANTES de enviar. Con el
    // celular vacío o no normalizable el form NO manda el request (ni
    // register() se llama), y se marca el campo para que el médico lo vea.
    const phoneCanonico = normalizeMobile(formData.phone, formData.country);
    if (!phoneCanonico) {
      setPhoneTouched(true);
      setErrorMessage(mensajeCelularInvalido(formData.country));
      return;
    }
    register(
      event,
      { ...formData, phone: phoneCanonico },
      setFormData,
      setErrorMessage,
    )
      .then(() => {
        // #1585: register() ya vació `password`; si el login de abajo falla,
        // la repetida llena dispararía "no coinciden" sobre una cuenta creada.
        setRepetirClave("");
        // #1525 R7 (reemplaza R18 de #1401): registro OK (correo o Google,
        // misma puerta) aterriza en el onboarding, /perfil/verificacion paso
        // Bienvenida, y no directo en /turno. La cuenta sigue naciendo con la
        // prueba corta concedida por el backend (#1401 R4): el onboarding es
        // opcional de punta a punta y desde ahí se llega al Turno con "Saltar"
        // o "Completar después".
        login(event, { ...formData, phone: phoneCanonico }, setFormData, setErrorMessage, {
          redirectTo: "/perfil/verificacion",
        });
      })
      .catch((error) => {
        // #1401, R17: register() YA dejó el mensaje visible con
        // setErrorMessage (errores por campo del backend, incluidos phone,
        // email y password) y YA rechazó la promesa — por eso este catch NO
        // llama a login(): un registro rechazado nunca intenta loguear.
        console.error("Registration error:", error?.code, error?.response?.status);
      });
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    // Si el navegador autocompletó el nombre de usuario, el backend responde 400
    // y el mensaje genérico no dice qué corregir. Lo atajamos acá.
    if (!looksLikeEmail(formData.email)) {
      setEmailTouched(true);
      setErrorMessage(
        "Ingresa tu correo electrónico (el mismo con el que creaste la cuenta), no tu nombre de usuario.",
      );
      return;
    }

    // El login puede tardar hasta 45s (arranque frío del backend). A los 8s
    // avisamos que seguimos intentando Y disparamos la sonda de red EN
    // PARALELO, para no dejar la pantalla muda: si es firewall, el médico tiene
    // el veredicto accionable mientras espera; si es arranque frío, la sonda
    // dice OK, el aviso dice "seguimos intentando" y el login entra igual.
    setErrorMessage("");
    setLoginPending(true);
    setLoginSlow(false);
    // Invalida el veredicto del intento anterior: el panel nunca debe mostrar
    // el diagnóstico de una corrida vieja.
    setLoginFailureKind(null);
    setLoginAttempt((n) => n + 1);
    const slowTimer = setTimeout(() => {
      setLoginSlow(true);
      setShowDiagnostics(true);
    }, SLOW_LOGIN_MS);

    let result;
    try {
      result = await login(event, formData, setFormData, setErrorMessage);
    } finally {
      clearTimeout(slowTimer);
      setLoginPending(false);
      setLoginSlow(false);
    }

    // Guardamos el tipo de fallo para que el panel no le diga "conexión OK,
    // revisá tu contraseña" a alguien cuyo login acaba de morir por lentitud.
    setLoginFailureKind(result ? result.kind : null);
    // 502/503 de un deploy (#1757 v2, R18): el banner global de mantenimiento lo explica, así que
    // el formulario no repite el error ni abre el diagnóstico de red. Si el banner NO se confirma
    // (un 502 aislado, la sonda responde bien) se queda el error de servidor de siempre, para que
    // el médico no se quede sin respuesta. El correo y la clave se conservan y «Ingresar» sigue activo.
    if (result && result.kind === "maintenance") {
      setErrorMessage(""); // sin parpadeo del error mientras el banner se confirma (≈1,5 s)
      setShowDiagnostics(false);
      if (!(await confirmarMantenimiento())) setErrorMessage(result.message);
      return;
    }
    // Si el fallo es de red, el médico no tiene por qué descubrir solo el botón
    // de diagnóstico: se lo abrimos nosotros.
    if (result && (result.kind === "network" || result.kind === "timeout")) {
      setShowDiagnostics(true);
    }
  };

  // #1453 R7/R11: la MISMA casilla de Términos para correo y para Google, en
  // la misma pantalla (sin pantalla aparte). En el registro va arriba, antes
  // de Google; en el login aparece junto a "Continuar con Google" solo cuando
  // el servidor dice que esa cuenta de Google es nueva.
  // Resalta la casilla, la trae a la vista y le pone el foco.
  const pedirCasilla = () => {
    setErrorMessage("");
    setTosResaltada(true);
    const el = casillaRef.current;
    if (el) {
      const reducir = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (el.scrollIntoView) el.scrollIntoView({ behavior: reducir ? "auto" : "smooth", block: "center" });
      const input = el.querySelector("input[type=checkbox]");
      if (input) input.focus({ preventScroll: true });
    }
  };

  // Casilla marcada con un Google pendiente: sigue sola. Va en un effect (no en
  // el onChange) porque el reenvío lee `tosAccepted` del render siguiente.
  useEffect(() => {
    if (formData.acceptTerms && googlePendienteRef.current) {
      const reintentar = googlePendienteRef.current;
      googlePendienteRef.current = null;
      reintentar();
    }
  }, [formData.acceptTerms]);

  // #1683 v2 (R58): sin casilla de WhatsApp. El permiso nace al aceptar los Términos, y el aviso
  // (`registro-aviso-whatsapp` / `login-aviso-whatsapp`, mockup v2 B) va pegado a esta casilla.
  const casillaTos = (testid) => {
    const alerta = tosResaltada && !formData.acceptTerms;
    const avisoId = testid === "registro-tos" ? "registro-aviso-whatsapp" : "login-aviso-whatsapp";
    return (
      <Box data-testid={`${testid}-conjunto`}>
      <Box
        ref={casillaRef}
        data-testid={testid}
        data-resaltada={alerta ? "true" : "false"}
        sx={{
          p: "13px 15px",
          borderRadius: "16px",
          border: "1.5px solid",
          borderColor: alerta
            ? "var(--color-danger)"
            : formData.acceptTerms
              ? "rgba(var(--fg-rgb),0.4)"
              : "rgba(var(--fg-rgb),0.2)",
          backgroundColor: alerta
            ? "rgba(var(--color-danger-rgb),0.08)"
            : "rgba(var(--fg-rgb),0.04)",
          transition: "border-color 160ms ease, background-color 160ms ease",
          animation: alerta ? "tosPulso 0.9s ease-in-out 2" : "none",
          "@keyframes tosPulso": {
            "0%, 100%": { boxShadow: "0 0 0 0 rgba(var(--color-danger-rgb),0)" },
            "50%": { boxShadow: "0 0 0 5px rgba(var(--color-danger-rgb),0.22)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
          <input
            type="checkbox"
            id={`${testid}-input`}
            name="acceptTerms"
            checked={formData.acceptTerms}
            aria-invalid={alerta ? "true" : "false"}
            aria-describedby={alerta ? `${testid}-aviso` : undefined}
            onChange={(e) => {
              handleChange(e);
              if (e.target.checked) setTosResaltada(false);
            }}
            style={{ marginTop: 2, accentColor: "var(--color-primary)", width: 20, height: 20, flex: "0 0 auto", cursor: "pointer" }}
          />
          <Typography
            component="label"
            htmlFor={`${testid}-input`}
            variant="body2"
            sx={{ fontSize: 13.5, lineHeight: 1.5, color: "text.primary", textAlign: "left", cursor: "pointer" }}
          >
            <b>Acepto los{" "}
            <Box
              component="span"
              onClick={(e) => {
                e.preventDefault();
                setOpenTermsModal(true);
              }}
              sx={{ color: "text.primary", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" }}
            >
              términos y condiciones
            </Box></b>
            <Box component="span" sx={{ color: "text.muted", fontSize: 12.5 }}>
              {" "}— SmartDx es una herramienta de apoyo médico y las decisiones
              finales son responsabilidad del profesional de la salud.
            </Box>
          </Typography>
        </Stack>
        {alerta && (
          <Typography
            id={`${testid}-aviso`}
            data-testid={`${testid}-aviso`}
            role="alert"
            sx={{ mt: 1, pl: "32px", fontSize: 13, fontWeight: 600, color: "var(--color-danger)", textAlign: "left" }}
          >
            {TOS_FALTA}
          </Typography>
        )}
      </Box>
      <AvisoWhatsapp testid={avisoId} lugar="registro" />
      </Box>
    );
  };

  // Registro: la casilla se exige antes de llamar (R7). Login: se llama sin
  // casilla pidiendo "solo cuenta existente" (R11 v2).
  const botonGoogle = (testid, { enRegistro }) => (
    <Box data-testid={testid} sx={{ minHeight: 44 }}>
      <GoogleSignInButton
        requireTos={enRegistro}
        soloCuentaExistente={!enRegistro}
        tosAccepted={Boolean(formData.acceptTerms)}
        bloqueadoPorTos={enRegistro && !formData.acceptTerms}
        onClickBloqueado={pedirCasilla}
        onTosMissing={(reintentar) => {
          pedirCasilla();
          if (reintentar) googlePendienteRef.current = reintentar;
        }}
        onNuevaCredencial={() => {
          // Corte real del cruce de cuentas: llega otra cuenta de Google (B) y
          // se descarta lo pendiente de A ANTES de saber cómo contesta el
          // servidor, así ni un 500 ni un corte de red de B dejan vivo el
          // reenvío de A. Test: alta-google-casilla.test.js "A sin casilla,
          // luego B falla (500)".
          googlePendienteRef.current = null;
          setCrearCuentaGoogle(null);
        }}
        onRequiereTerminos={(reintentar) => {
          setErrorMessage("");
          // Respaldo: el corte del cruce A→B está en onNuevaCredencial, que ya
          // borró esto antes del POST. Acá se vuelve a limpiar por las dudas.
          googlePendienteRef.current = null;
          setTosResaltada(false);
          setCrearCuentaGoogle(() => reintentar);
        }}
        showDivider={false}
        temaGoogle="outline"
      />
    </Box>
  );

  const separador = (texto) => (
    <Divider
      sx={{
        color: "rgba(var(--fg-rgb),0.5)",
        fontSize: 13,
        "&::before, &::after": { borderColor: "rgba(var(--fg-rgb),0.16)" },
      }}
    >
      {texto}
    </Divider>
  );

  // type="button" obligatorio: un <button> dentro del <form> es submit por
  // defecto y apretar el ojito mandaría el login o el registro.
  const ojoClave = (testid) => (
    <IconButton
      type="button"
      data-testid={testid}
      onClick={() => setClaveVisible((v) => !v)}
      aria-label={claveVisible ? "Ocultar la contraseña" : "Mostrar la contraseña"}
      aria-pressed={claveVisible}
      sx={{
        position: "absolute",
        right: 4,
        top: "50%",
        transform: "translateY(-50%)",
        color: "text.muted",
        // 44pt de área tocable, igual que en PasswordFields.
        minWidth: 44,
        minHeight: 44,
      }}
    >
      {claveVisible ? (
        <VisibilityOffOutlinedIcon fontSize="small" />
      ) : (
        <VisibilityOutlinedIcon fontSize="small" />
      )}
    </IconButton>
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!currentUser ? (
        <>
          {/* #1439: las pestañas reemplazan al link "¿Ya tienes cuenta? /
              ¿Quieres probar SmartDx?" del pie. Son role="tab" (no botones)
              para no chocar con el botón de enviar "Crear cuenta". */}
          <Box
            role="tablist"
            aria-label="Crear cuenta o ingresar"
            sx={{
              display: "grid", gridTemplateColumns: "1fr 1fr", p: "4px", borderRadius: "999px",
              backgroundColor: "rgba(var(--fg-rgb),0.08)", border: "1px solid rgba(var(--fg-rgb),0.14)",
            }}
          >
            <Box component="button" type="button" role="tab" data-testid="acceso-tab-crear"
              aria-selected={registrationToggle} onClick={() => cambiarModo(true)} sx={pestanaSx(registrationToggle)}>
              Crear cuenta
            </Box>
            <Box component="button" type="button" role="tab" data-testid="acceso-tab-ingresar"
              aria-selected={!registrationToggle} onClick={() => cambiarModo(false)} sx={pestanaSx(!registrationToggle)}>
              Ingresar
            </Box>
          </Box>
          {/* #1439 C (mockup aprobado, versión chica): confirma la unidad que eligió en el
              pop-up "Qué te trae a SmartDx?" de la home. "cambiar" vuelve a la home con el
              pop-up disponible otra vez. No pide ningún dato nuevo. */}
          {registrationToggle && motivoActual(location.search) && (
            <Box
              data-testid="registro-motivo"
              sx={{
                display: "inline-flex", alignItems: "center", gap: 1, mt: { xs: 2, md: 2.25 },
                pl: "6px", pr: "12px", py: "5px", borderRadius: "999px", fontSize: 13, fontWeight: 500,
                color: "var(--color-secondary)",
                backgroundColor: "rgba(var(--color-secondary-rgb),0.12)",
                border: "1px solid rgba(var(--color-secondary-rgb),0.3)",
              }}
            >
              <Box component="span" aria-hidden="true" sx={{ width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: "rgba(var(--color-secondary-rgb),0.2)" }}>
                <CheckRoundedIcon sx={{ fontSize: 14 }} />
              </Box>
              <span>
                Para tu turno en <b>{MOTIVO_TEXTO[motivoActual(location.search)]}</b>
              </span>
              <Box
                component="a"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  try {
                    window.sessionStorage.removeItem("sdx-pop");
                    window.sessionStorage.removeItem("sdx-motivo");
                  } catch (err) {
                    /* sin sessionStorage igual vuelve a la home */
                  }
                  navigate("/");
                }}
                // nowrap: a 320 con "Consulta ambulatoria" el "·" y "cambiar" quedaban en líneas distintas.
                sx={{ color: "inherit", opacity: 0.7, textDecoration: "none", whiteSpace: "nowrap", "&:hover, &:focus-visible": { opacity: 1, textDecoration: "underline" } }}
              >
                · cambiar
              </Box>
            </Box>
          )}
          <Box sx={{ mt: { xs: 2, md: 2.75 }, mb: { xs: 2, md: 2.5 } }}>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: 28, md: 36 }, lineHeight: 1.06, letterSpacing: "-0.035em", fontWeight: 500, color: "text.primary" }}
            >
              {registrationToggle ? "Crea tu cuenta" : "Hola de nuevo"}
              <Box component="span" sx={{ display: "block", color: "rgba(var(--fg-rgb),0.55)" }}>
                {registrationToggle ? "en un minuto" : "tu turno te espera"}
              </Box>
            </Typography>
            <Typography sx={{ color: "rgba(var(--fg-rgb),0.7)", fontSize: { xs: 14.5, md: 15.5 }, mt: 0.75 }}>
              {registrationToggle
                ? "Gratis para empezar, sin tarjeta."
                : "La misma cuenta del celular y del computador."}
            </Typography>
          </Box>

          {/* Aviso verde tras cambiar la clave desde el enlace del correo. Sin
              esto el médico vuelve al login sin ninguna señal de que su cambio
              funcionó, y prueba de nuevo con la clave vieja. */}
          {location.state?.flash === "password-reset" && (
            <Box
              sx={{
                width: "100%",
                mb: 2,
                px: 1.6,
                py: 1.3,
                borderRadius: "11px",
                backgroundColor: "rgba(var(--color-success-rgb),0.10)",
                border: "1px solid rgba(var(--color-success-rgb),0.28)",
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: "var(--color-success)", fontWeight: 600 }}>
                Clave actualizada. Entra con la nueva.
              </Typography>
            </Box>
          )}

          {/* noValidate: la validación del correo la hacemos nosotros para poder
              explicar en español que se entra con el correo y no con el usuario;
              el burbujeo nativo del navegador tapaba ese mensaje. */}
          <form
            noValidate
            onSubmit={registrationToggle ? submitRegistration : submitLogin}
            style={{ width: "100%" }}
          >
            <Stack spacing={2}>
              {/* #1453 R7: en el registro, Términos + Google primero (camino
                  principal) y el correo como alternativa. Sin campo de
                  nombre de usuario (R6). */}
              {registrationToggle && casillaTos("registro-tos")}
              {registrationToggle && botonGoogle("registro-google", { enRegistro: true })}
              {registrationToggle && GOOGLE_DISPONIBLE && separador("o con tu correo")}
              {/* #1453 R11 v2: en el login, Google sin casilla. La casilla
                  aparece junto a Google solo si la cuenta resulta nueva.
                  #1439: Google va primero también al ingresar (mockup). */}
              {!registrationToggle && GOOGLE_DISPONIBLE && (
                <>
                  {botonGoogle("login-google", { enRegistro: false })}
                  {crearCuentaGoogle && (
                    <Stack data-testid="login-google-nueva" spacing={1.25}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "center" }}>
                        {TOS_CUENTA_NUEVA_GOOGLE}
                      </Typography>
                      {casillaTos("login-tos")}
                      <Button
                        data-testid="login-google-crear"
                        variant="contained"
                        disableElevation
                        sx={primaryBtnSx}
                        onClick={() => {
                          // Este click consume el reenvío guardado: no hay
                          // otro que salga solo al marcar después.
                          googlePendienteRef.current = null;
                          crearCuentaGoogle();
                        }}
                      >
                        Crear mi cuenta y entrar
                      </Button>
                    </Stack>
                  )}
                  {separador("o con tu correo")}
                </>
              )}
              <Box>
                <Typography component="label" htmlFor="email" sx={fieldLabelSx}>
                  Correo electrónico
                </Typography>
                <Box
                  component="input"
                  id="email"
                  type="email"
                  name="email"
                  placeholder="tu@correo.cl"
                  value={formData.email}
                  onChange={handleChange}
                  // El correo ES el identificador de la cuenta: marcarlo como
                  // "username" hace que el gestor guarde/rellene el correo (y no
                  // el nombre de usuario) en el primer inicio de sesión.
                  autoComplete="username"
                  // El login del backend es case-sensitive en el correo y el
                  // teclado móvil autocapitaliza la primera letra: defensa en
                  // profundidad para que no llegue "Dr@..." en vez de "dr@...".
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  // Solo al salir del campo: si avisáramos en cada tecla, el
                  // aviso aparecería mientras el médico escribe su correo.
                  onBlur={() => setEmailTouched(true)}
                  required
                  sx={inputVidrioSx}
                />
                {!registrationToggle && emailTouched && formData.email && !looksLikeEmail(formData.email) && (
                  <Typography sx={{ fontSize: 12, color: "text.muted", mt: 0.75, textAlign: "left" }}>
                    Se ingresa con el <b>correo electrónico</b>, no con el nombre de usuario.
                  </Typography>
                )}
              </Box>
              <Box>
                {/* El link va en la fila del label, no abajo con el toggle de
                    registro: es donde está el ojo del médico justo cuando la
                    clave falla.

                    Es un <a> de router y con type="button" explícito. Un
                    <button> dentro del <form> es submit por defecto y
                    dispararía el login, que es la misma trampa que ya está
                    documentada para ConnectionDiagnostics más abajo. */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography component="label" htmlFor="password" sx={fieldLabelSx}>
                    Contraseña
                  </Typography>
                  {!registrationToggle && (
                    <Button
                      component={RouterLink}
                      to="/recuperar-clave"
                      type="button"
                      variant="text"
                      sx={{
                        p: 0,
                        minWidth: 0,
                        mb: 0.85,
                        textTransform: "none",
                        fontSize: 12,
                        // Cuando la clave es la que falló, el link se refuerza.
                        // Con error de RED no: mandar a recuperar la clave a
                        // alguien cuyo hospital le bloquea el backend es el bug
                        // equivocado.
                        fontWeight: loginFailureKind === "credentials" ? 600 : 500,
                        color: "var(--color-info)",
                        "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                      }}
                    >
                      Olvidaste tu clave
                    </Button>
                  )}
                </Stack>
                <Box sx={{ position: "relative" }}>
                  <Box
                    component="input"
                    id="password"
                    type={claveVisible ? "text" : "password"}
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete={registrationToggle ? "new-password" : "current-password"}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    sx={{ ...inputVidrioSx, pr: "48px" }}
                  />
                  {ojoClave("clave-ojo")}
                </Box>
                {registrationToggle && formData.password && (
                  <Box
                    component="ul"
                    data-testid="registro-clave-reglas"
                    aria-live="polite"
                    sx={{ listStyle: "none", p: 0, m: 0, mt: 0.75, display: "flex", flexDirection: "column", gap: 0.25 }}
                  >
                    {reglas.map((r) => (
                      <Box
                        component="li"
                        key={r.id}
                        data-regla={r.id}
                        data-ok={String(r.ok)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          fontSize: 12,
                          textAlign: "left",
                          color: r.ok ? "success.main" : "text.muted",
                        }}
                      >
                        {r.ok ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 15 }} />
                        )}
                        {r.texto}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {registrationToggle && (
                // #1585 (mockup tools/mockups/registro_repetir_clave.html, A):
                // sin repetir, un error de tipeo deja al médico afuera de la
                // cuenta que acaba de crear. Solo avisa si no coinciden.
                <Box>
                  <Typography component="label" htmlFor="repeat-password" sx={fieldLabelSx}>
                    Repite la contraseña
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <Box
                      component="input"
                      id="repeat-password"
                      type={claveVisible ? "text" : "password"}
                      name="repeat-password"
                      placeholder="••••••••"
                      value={repetirClave}
                      onChange={(e) => setRepetirClave(e.target.value)}
                      autoComplete="new-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      required
                      data-testid="registro-repetir-clave"
                      sx={{ ...inputVidrioSx, pr: "48px" }}
                    />
                    {ojoClave("repetir-clave-ojo")}
                  </Box>
                  {repetirClave && formData.password && !claveCoincide && (
                    <Typography
                      data-testid="registro-clave-no-coincide"
                      sx={{ fontSize: 12, color: "error.main", fontWeight: 600, mt: 0.75, textAlign: "left" }}
                    >
                      Las dos contraseñas no coinciden.
                    </Typography>
                  )}
                </Box>
              )}

              {registrationToggle && (
                // #1590: país aparte, arriba del celular (mockup Parte 1 A).
                // Select nativo: en el celular abre la rueda del sistema.
                // Cubre R1 — .claude/specs/1590-paises-telefono-usd/
                <Box>
                  <Typography component="label" htmlFor="country" sx={fieldLabelSx}>
                    País
                  </Typography>
                  <Box
                    component="select"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    autoComplete="country"
                    data-testid="registro-pais"
                    sx={{
                      ...inputVidrioSx,
                      width: "100%",
                      cursor: "pointer",
                      // La lista nativa no hereda el vidrio: sin esto Chrome
                      // pinta texto claro sobre fondo blanco del sistema.
                      "& option": { backgroundColor: "var(--bg-elevated)", color: "var(--text-primary)" },
                    }}
                  >
                    {PAISES.map((p) => (
                      <option key={p.iso} value={p.iso}>
                        {`${banderaPais(p.iso)} ${p.nombre} (+${p.code})`}
                      </option>
                    ))}
                  </Box>
                </Box>
              )}

              {registrationToggle && (
                <Box>
                  <Typography component="label" htmlFor="phone" sx={fieldLabelSx}>
                    Celular
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="stretch">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                        borderRadius: "14px",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "text.muted",
                        backgroundColor: "rgba(var(--fg-rgb),0.08)",
                        border: "1px solid rgba(var(--fg-rgb),0.16)",
                        flex: "0 0 auto",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {prefijoCelular(formData.country)}
                    </Box>
                    <Box
                      component="input"
                      id="phone"
                      type="tel"
                      inputMode="tel"
                      name="phone"
                      placeholder={placeholderCelular(formData.country)}
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => setPhoneTouched(true)}
                      autoComplete="tel-national"
                      required
                      data-testid="registro-celular"
                      sx={{ ...inputVidrioSx, flex: "1 1 auto" }}
                    />
                  </Stack>
                  {phoneTouched && formData.phone && !normalizeMobile(formData.phone, formData.country) && (
                    <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.75, textAlign: "left" }}>
                      {mensajeCelularInvalido(formData.country)}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 12, color: "text.muted", mt: 0.75, textAlign: "left" }}>
                    Lo usamos para verificar tu cuenta y para ayudarte a usar SmartDx.
                  </Typography>
                </Box>
              )}

            </Stack>

            {errorMessage && (
              <Typography
                data-testid={registrationToggle ? "registro-error" : "login-error"}
                sx={{ color: "error.main", mt: 2, fontWeight: 600, fontSize: 13, textAlign: "center" }}
              >
                {errorMessage}
              </Typography>
            )}

            {loginSlow && (
              <Typography
                sx={{ color: "text.muted", mt: 2, fontSize: 12.5, lineHeight: 1.55, textAlign: "center" }}
              >
                Esto está tardando más de lo normal. Seguimos intentando entrar,
                no cierres esta página.
              </Typography>
            )}

            <Stack spacing={1.75} sx={{ mt: 2.25 }}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                sx={enviarSx}
                disabled={!isFormValid() || loginPending}
              >
                {registrationToggle
                  ? "Crear cuenta"
                  : loginPending
                    ? "Ingresando..."
                    : "Ingresar"}
              </Button>
            </Stack>
            {registrationToggle && (
              <Typography
                data-testid="registro-aviso-turno"
                sx={{
                  fontSize: 12,
                  lineHeight: 1.55,
                  textAlign: "left",
                  mt: 2,
                  p: 1.5,
                  borderRadius: "14px",
                  color: "rgba(var(--fg-rgb),0.6)",
                  backgroundColor: "rgba(var(--fg-rgb),0.06)",
                  border: "1px solid rgba(var(--fg-rgb),0.14)",
                }}
              >
                {/* Lo que dice acá tiene que ser lo que hace el backend. Desde
                    #1401 el Modo Turno queda habilitado AL CREAR la cuenta, con
                    una prueba de 14 días que arranca con el primer documento
                    generado. Desde #1529 subir cédula y certificado ya no
                    bloquea ni suspende nada: el Turno solo lo recuerda. Por eso
                    el texto no condiciona el acceso a los documentos (R16, texto
                    aprobado por Sebastián el 02-oct-2026).
                    #1604: la cédula y el certificado son solo para Chile; con otro
                    país elegido arriba el aviso no los menciona (pideVerificacion). */}
                Tu Modo Turno queda habilitado apenas creas tu cuenta, con una prueba
                de 14 días que arranca con tu primer documento generado.
                {pideVerificacion({ country: formData.country }) &&
                  // #1850: los documentos ya no se piden en el alta; se piden al suscribirse.
                  " Cuando te suscribas te pediremos tu cédula y, si ya estás titulado, " +
                    "tu certificado de la Superintendencia de Salud."}
              </Typography>
            )}
          </form>

          {/* Autodiagnóstico de red. Va FUERA del <form> a propósito: un botón
              adentro podría disparar el submit del login. */}
          {!registrationToggle && (
            <ConnectionDiagnostics
              open={showDiagnostics}
              onToggle={setShowDiagnostics}
              loginState={loginPending ? "pending" : loginFailureKind}
              attempt={loginAttempt}
            />
          )}

          {/* Contacto directo — solo al ingresar (#1439, mockup). */}
          {!registrationToggle && (
          <Box sx={{ mt: 2.5 }}>
            <Divider sx={{ "&::before, &::after": { borderColor: "rgba(var(--fg-rgb),0.16)" } }}>
              <Typography sx={{ fontSize: 13, color: "rgba(var(--fg-rgb),0.5)", px: 1 }}>
                o háblame directo
              </Typography>
            </Divider>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {CONTACT_CHANNELS.map((c) => {
                const isRoute = Boolean(c.route);
                return (
                  <Button
                    key={c.label}
                    component={isRoute ? "button" : "a"}
                    href={isRoute ? undefined : c.href}
                    target={c.external ? "_blank" : undefined}
                    rel={c.external ? "noopener noreferrer" : undefined}
                    onClick={isRoute ? () => navigate({ pathname: c.route, hash: c.hash }) : undefined}
                    startIcon={c.icon}
                    sx={channelBtnSx}
                  >
                    {c.label}
                  </Button>
                );
              })}
            </Stack>
          </Box>
          )}
        </>
      ) : currentUser.has_access ? (
        // #1401, R21: ya no siempre a /home — una cuenta con SOLO el Modo
        // Turno (la prueba corta, R4/R5) aterriza directo en /turno.
        <Navigate to={destinoSegunCuenta(currentUser)} replace />
      ) : (
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6" textAlign="center">
            Hola, {currentUser.username} 👋
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ color: "text.secondary" }}>
            Te falta completar tu verificación.
            {/* #1604: la cédula y el certificado son solo para Chile. */}
            {pideVerificacion(currentUser.professional_profile) &&
              " Con tu cédula (y tu certificado de la Superintendencia si ya estás titulado) " +
                "tu cuenta queda habilitada al momento."}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            href="/perfil/verificacion"
            sx={{ ...primaryBtnSx, mt: 1 }}
          >
            Completar mi verificación
          </Button>
          <Button
            onClick={logout}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 600, color: "error.main" }}
          >
            Cerrar sesión
          </Button>
        </Stack>
      )}

      {/* Modal de términos */}
      <Dialog
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        // Dentro del alcance oscuro de AuthShell: un portal al <body> perdería
        // las CSS vars del modo oscuro y en modo claro saldría mezclado.
        container={authModalRootEl}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: "background.paper",
            backgroundImage: "none",
            color: "text.primary",
            borderRadius: "20px",
            maxHeight: "80vh",
            marginBottom: "40px",
            overflowY: "auto",
            // Mockup B: sin borde, modal flota con hairline + sombra profunda.
            border: "none",
            boxShadow:
              "0 0 0 1px rgba(var(--fg-rgb),0.06), 0 24px 60px rgba(var(--shadow-rgb),0.55)",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "background.paper", color: "text.primary", p: 1.5, minHeight: 0 }}>
          <IconButton
            aria-label="close"
            onClick={() => setOpenTermsModal(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              zIndex: 1,
              // Mockup B: botón cerrar glass (theme-safe vía --fg-rgb).
              color: "text.primary",
              backgroundColor: "rgba(var(--fg-rgb),0.08)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(var(--fg-rgb),0.14)",
              "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.16)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "background.paper", color: "text.primary", pt: 0 }}>
          {/* #1401, R19: el mismo texto click-through Tipo A que muestra
              TosAcceptanceGate (mismo `getTosMarkdown` + RichMarkdown). Sin usuario todavía no
              hay `current_tos_version`: se pide TOS_VERSION_VIGENTE, la que el backend registra
              al marcar la casilla (settings.CURRENT_TOS_VERSION). Sin versión saldría el texto
              congelado de la v1.x (revisor del bump, 20-oct-2026, A1). */}
          <RichMarkdown headingBars={false}>{getTosMarkdown(TOS_VERSION_VIGENTE)}</RichMarkdown>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuthenticationForm;
